import IconClose from "@/components/@icons/IconClose.vue";
import TopArrow from "@/components/@icons/TopArrow.vue";
import BottomArrow from "@/components/@icons/BottomArrow.vue";

export default {
  name: "ProductGallery",
  components: { IconClose, TopArrow, BottomArrow },
  props: {
    images: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: () => "",
    },
    subtitle: {
      type: String,
      default: () => "",
    },
  },
  data() {
    return {
      currentIndex: 0,
      isZoomed: false,
      zoomLevel: 1,
      cursorX: 50,
      cursorY: 50,
      isTouchDevice: false,
      panX: 0,
      panY: 0,
      touchStartDistance: null,
      touchStartZoom: 1,
      touchStartPanX: 0,
      touchStartPanY: 0,
      touchStartX: 0,
      touchStartY: 0,
      isMultiTouch: false,
    };
  },
  mounted() {
    this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  },
  computed: {
    currentImage() {
      return this.images[this.currentIndex] || "";
    },
    imageStyle() {
      if (this.isTouchDevice && this.zoomLevel > 1) {
        return {
          transform: `translate(${this.panX}px, ${this.panY}px) scale(${this.zoomLevel})`,
          transformOrigin: 'center center',
          transition: 'none',
        };
      }

      if (!this.isTouchDevice && this.isZoomed) {
        return {
          transform: `scale(2.5)`,
          transformOrigin: `${this.cursorX}% ${this.cursorY}%`,
          cursor: "zoom-in",
        };
      }

      return {};
    },
  },
  methods: {
    selectImage(index) {
      this.currentIndex = index;
      this.resetZoom();
    },
    handleNextImage() {
      const max = this.images.length - 1;
      this.currentIndex = (this.currentIndex + 1) % (max + 1);
      this.resetZoom();
    },
    handlePrevImage() {
      const max = this.images.length - 1;
      this.currentIndex = (this.currentIndex - 1 + max) % (max + 1);
      this.resetZoom();
    },
    resetZoom() {
      this.isZoomed = false;
      this.zoomLevel = 1;
      this.cursorX = 50;
      this.cursorY = 50;
      this.panX = 0;
      this.panY = 0;
      this.touchStartDistance = null;
      this.touchStartZoom = 1;
      this.touchStartPanX = 0;
      this.touchStartPanY = 0;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.isMultiTouch = false;
    },
    handleMouseMove(event) {
      if (!this.isZoomed || this.isTouchDevice) return;

      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      this.cursorX = (x / rect.width) * 100;
      this.cursorY = (y / rect.height) * 100;
    },
    handleMouseEnter() {
      if (this.isTouchDevice) return;
      this.isZoomed = true;
    },
    handleMouseLeave() {
      if (this.isTouchDevice) return;
      this.isZoomed = false;
      this.cursorX = 50;
      this.cursorY = 50;
    },
    getDistance(touch1, touch2) {
      const dx = touch1.clientX - touch2.clientX;
      const dy = touch1.clientY - touch2.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    },
    getTouchCenter(touch1, touch2) {
      return {
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2,
      };
    },
    handleTouchStart(event) {
      const touchCount = event.touches.length;

      if (touchCount === 2) {
        event.preventDefault();
        this.isMultiTouch = true;

        this.touchStartZoom = this.zoomLevel;

        this.touchStartDistance = this.getDistance(event.touches[0], event.touches[1]);

        this.touchStartPanX = this.panX;
        this.touchStartPanY = this.panY;

        const center = this.getTouchCenter(event.touches[0], event.touches[1]);

        const container = event.currentTarget;
        const rect = container.getBoundingClientRect();

        this.touchStartX = center.x - rect.left - rect.width / 2;
        this.touchStartY = center.y - rect.top - rect.height / 2;
      } else if (touchCount === 1) {
        this.isMultiTouch = false;

        if (this.zoomLevel > 1) {
          event.preventDefault();
          const touch = event.touches[0];

          this.touchStartX = touch.clientX;
          this.touchStartY = touch.clientY;
          this.touchStartPanX = this.panX;
          this.touchStartPanY = this.panY;
        }
      }
    },
    handleTouchMove(event) {
      const touchCount = event.touches.length;

      if (touchCount === 2 && this.isMultiTouch) {
        event.preventDefault();

        const currentDistance = this.getDistance(event.touches[0], event.touches[1]);

        const scale = currentDistance / this.touchStartDistance;

        let newZoom = this.touchStartZoom * scale;
        newZoom = Math.max(1, Math.min(5, newZoom));

        const zoomDelta = newZoom - this.touchStartZoom;

        this.panX = this.touchStartPanX - this.touchStartX * zoomDelta;
        this.panY = this.touchStartPanY - this.touchStartY * zoomDelta;

        this.zoomLevel = newZoom;
      } else if (touchCount === 1 && !this.isMultiTouch && this.zoomLevel > 1) {
        event.preventDefault();

        const touch = event.touches[0];
        const deltaX = touch.clientX - this.touchStartX;
        const deltaY = touch.clientY - this.touchStartY;

        this.panX = this.touchStartPanX + deltaX;
        this.panY = this.touchStartPanY + deltaY;
      }
    },
    handleTouchEnd(event) {
      if (event.touches.length < 2) {
        this.isMultiTouch = false;
      }

      if (event.touches.length === 1 && this.zoomLevel > 1) {
        const touch = event.touches[0];
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
        this.touchStartPanX = this.panX;
        this.touchStartPanY = this.panY;
      }

      if (event.touches.length === 0 && this.zoomLevel <= 1) {
        this.resetZoom();
      }
    },
    close() {
      this.$emit("close");
    },
  },
};
